import { useDraggable } from "@dnd-kit/core";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  getDebugInfoText,
  getMDIcon,
  getMdOptions,
  getTitleData,
} from "./helpers";
import {
  MouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { setSelectedCarouselIndex } from "redux/Modals/actions";
import {
  clearImagePageParamsContext,
  fetchClusterPageIssueTypes,
  setCardslistLoading,
  setClusterPageScrollPosition,
  setDataPageScrollPosition,
  setNavigationCluster,
  setNavigationClusterLoader,
  setSimilarityData,
  setUserTags,
} from "redux/SingleDataset/actions";

import {
  addQueryParameter,
  copyToClipboard,
  getInitialMDType,
  getMediaDimensions,
  useWindowResize,
} from "helpers/utility/utilities";
import { DATASET_CREATION_PAGE } from "helpers/constants/pages";
import {
  DATA_PAGE_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  FLAT_RESPONSE_PARAMETER,
  LABEL_CLASS_PARAMETER,
  LABELS_PARAMETER,
  LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
  METADATA_SOURCES_PARAMETER,
  OBJECT_ID_QUERY_PARAMETER,
  PAGE_PARAMETER,
  REVIEW_PARAMETER,
  USER_TAGS_PARAMETER,
} from "helpers/constants/miscellaneous";
import {
  CardBodyImage,
  CardTypes,
  CardVariant,
  DataPreview,
  FilterOption,
  HandleSelect,
  isValidEntityType,
  LabelsFilter,
  MetadataType,
  QueryOperator,
  RelevanceType,
  SetStateFunction,
  SingleExportItem,
  TagsFilter,
  VqlQueryBase,
} from "types";
import { AnnotatedBoundingBox } from "extractedBackendTypes";

import AssetPreviewCard from "views/components/AssetPreviewCard";
import FindSimilarIcon from "assets/icons/FindSimilarIcon";
import CheckIcon from "assets/icons/CheckIcon";
import LinkIcon from "assets/icons/LinkIcon";
import XIcon from "assets/icons/XIcon";
import CardHeader from "./CardHeader";
import CardBody from "./CardBody";

import styles from "./style.module.scss";
import classNames from "classnames";
import useQueryParam from "hooks/useQueryParam";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import useEntityTypeFromQuery from "hooks/useEntityTypeFromQuery";
import ReviewCardHeader from "./ReviewCardHeader";
import {
  createAndUpdateLabelsFilter,
  createAndUpdateUserTagsFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import useVqlParams from "hooks/useVqlParams";
import ImageIcon from "assets/icons/imageIcon";
import { Cluster, PreviewBorderColor } from "../index";
import VLFloatGroup from "views/uikit/VLFloatGroup";
import ReviewFloatingDetails from "../../../../../components/LabelPropagation/ReviewFloatingDetails";
import { getVLColorByActionColor } from "../../ReviewList/utils";
import ReloadIcon from "assets/icons/ReloadIcon";
import {
  getDatasetContextualLabels,
  getIsCaptionsFilterEnabled,
  getIsSearchByRelevanceEnabled,
} from "redux/SingleDataset/selectors";
import VisualSimilarityPopover from "views/components/VisualSimilarityPopover";
import { getIsAddMediaEnabled } from "redux/Datasets/selectors";
import intersection from "lodash/intersection";
import { EnrichmentCardProvider } from "./EnrichmentCardProvider";
import { Tooltip } from "antd";

export interface EnrichmentCardProps {
  page: string;
  index: number;
  singleData: any;
  userTagsData: any;
  datasetId: string;
  clusterID: string;
  isVertex: boolean;
  isDataPage: boolean;
  cardElementRef?: any;
  isClusterPage: boolean;
  isVertexCard?: boolean;
  indexOfPointerOut: number;
  showSelectOption?: boolean;
  showMetadataSummary: boolean;
  modelType?: "IMAGE" | "OBJECT";
  globalMDType: MetadataType | null;
  containerRef: RefObject<HTMLDivElement>;

  clusters?: any[];
  showDebugInfo?: boolean;
  isCardslistLoading?: boolean;
  datasetLabels?: FilterOption[];
  exportData?: SingleExportItem[];
  datasetUserTags?: FilterOption[];
  relevanceScores?: number[] | null;
  isNavigationClusterLoading?: boolean;
  navigationCluster?: any;

  onSelect: HandleSelect;
  onRemoveVertex?: (e: MouseEvent) => void;
  onIndexOfPointerOut: SetStateFunction<number>;
  onGlobalMDTypeChange: (type: MetadataType) => void;
  onSetVertex?: (
    id: string,
    type: "MEDIA" | "CLUSTER",
    isExclude?: boolean,
  ) => void;
  onSetVertexSimilarity?: (threshold: string) => void;
  cardTitle?: string;
  showFeedbackInCard?: boolean;
  disableInteractivity?: boolean;
  className?: string;
  columnsCount?: number;
}

//temporary added the flag to disable visual similarity threshold feature
const isVisualSimilarityEnabled = false;

const EnrichmentCard = (props: EnrichmentCardProps) => {
  const {
    index,
    page,
    isVertex,
    clusterID,
    modelType,
    isDataPage,
    singleData,
    userTagsData,
    isVertexCard,
    containerRef,
    globalMDType,
    isClusterPage,
    showSelectOption,
    indexOfPointerOut,
    showMetadataSummary,

    clusters = [],
    exportData = [],
    datasetLabels = [],
    datasetUserTags = [],
    showDebugInfo = false,
    relevanceScores = null,
    isCardslistLoading = false,
    isNavigationClusterLoading = false,
    navigationCluster,

    onSelect,
    onSetVertex,
    onSetVertexSimilarity,
    onRemoveVertex,
    onIndexOfPointerOut,
    onGlobalMDTypeChange,
    cardTitle,
    showFeedbackInCard,
    disableInteractivity = false,
    className,
    columnsCount,
  } = props;
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: index,
    data: {
      ...singleData,
      exportType: isDataPage ? "cluster" : "media",
      props,
      trigger: "drag",
    },
  });

  const [isHovering, setIsHovering] = useState<any>(false);
  const [isReviewActionOpen, setIsReviewActionOpen] = useState(false);
  const [mdLabels, setMdLabels] = useState<string[] | null>(null);
  const { addVqlQuery, vqlQueries, updateVqlQuery, removeVqlQuery } =
    useVqlParams();

  const entitySearchParam = useQueryParam(ENTITY_TYPE_PARAMETER);
  const entityParams = isValidEntityType(entitySearchParam)
    ? entitySearchParam
    : null;

  const queryParamsFilters = useAllQueryParamsFilters();
  const [linkClicked, setLinkClicked] = useState(false);
  const [hoveredAssetData, setHoveredAssetData] = useState({
    id: "",
    dimensions: { naturalHeight: 0, naturalWidth: 0 },
    error: false,
  });
  const { datasetId, clusterId, enrichDatasetId, newDatasetId } = useParams();
  const assetPreviewFlagRef = useRef(true);
  const { width } = useWindowResize();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const entityType = useEntityTypeFromQuery();
  const isSearchByRelevanceEnabled = useSelector((state) =>
    getIsSearchByRelevanceEnabled(state, entityType),
  );
  const isCaptionsVisible = useSelector(getIsCaptionsFilterEnabled);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const uniqueClusterWrapperId = singleData.__id;

  const isDataCreationPage = page === DATASET_CREATION_PAGE;

  const isObjectModel = modelType === "OBJECT";
  const bodyEntityType: CardTypes = singleData.type;
  const inReview = searchParams.has(REVIEW_PARAMETER);
  const inReviewDetails = searchParams.has(LABEL_CLASS_PARAMETER);
  const isPreview = singleData?.cluster_id;

  const showFloatingDetails = !!singleData.floatingDetails && inReviewDetails;
  const hideMetadataOptions = isDataCreationPage || disableInteractivity;
  const showActionButtons = isHovering && !hideMetadataOptions;

  const isBodyUnclickable = !!enrichDatasetId || isVertex || !!newDatasetId;

  const isSingleEntityOnDataPage =
    isDataPage && singleData.previews.length === 1;

  const firstPreview = singleData.previews?.[0];

  const fileName = hideMetadataOptions
    ? singleData.file_name ||
      singleData.original_uri ||
      firstPreview?.file_name ||
      firstPreview?.original_uri
    : isSingleEntityOnDataPage
      ? firstPreview.video_uri
        ? firstPreview.video_uri.split("/")[
            firstPreview.video_uri.split("/").length - 1
          ]
        : firstPreview.file_name
      : isClusterPage
        ? singleData.video_uri
          ? singleData.video_uri.split("/")[
              singleData.video_uri.split("/").length - 1
            ]
          : singleData.file_name
        : "";

  const formattedFilename = decodeURIComponent(fileName);

  const getCreationPageLabels = (): string[] => {
    const preview: DataPreview = singleData;
    let objectAnnotations = preview.object_annotations || [];
    let imageAnnotations = preview.image_annotations || [];

    if (!!objectAnnotations.length) {
      return objectAnnotations
        .map((singleAnnotation) =>
          singleAnnotation.annotations ? singleAnnotation.annotations[0] : null,
        )
        .filter((item): item is string => typeof item === "string");
    }

    return imageAnnotations;
  };

  const getCreationBoundingBoxes = (): number[][] => {
    let objectAnnotations: AnnotatedBoundingBox[] =
      singleData.object_annotations || [];

    if (!!objectAnnotations.length) {
      return objectAnnotations
        .map((annotation) => {
          let { col_x, row_y, width, height } = annotation;
          if (col_x && row_y && width && height) {
            return [col_x, row_y, width, height];
          } else return [];
        })
        .slice(0, 5);
    } else {
      return [];
    }
  };

  const getDataStrings = (
    data: any,
    userTagsData: any,
  ): {
    labels: string[];
    userTags: string[];
    captions: string[];
  } => {
    if (hideMetadataOptions || inReview) {
      return { labels: [], userTags: [], captions: [] };
    }

    if (hoveredAssetData.id) {
      const assetData = getAssetData();
      return {
        labels: assetData.labels || [],
        userTags: assetData.user_tags || [],
        captions: assetData.caption ? [assetData.caption] : [],
      };
    }

    const isMedia = isClusterPage || data.flat_response;
    if (data.flat_response) {
      data = data.previews[0];
    }
    const isCluster = data.cluster_id;

    const labels = isCluster
      ? Object.keys(data.labels)
      : isMedia
        ? data.labels || []
        : [];

    const userTags = isCluster
      ? Object.keys(userTagsData)
      : isMedia
        ? userTagsData || []
        : [];

    const captions = isCluster
      ? data.captions || []
      : data.caption
        ? [data.caption]
        : [];

    return { labels, userTags, captions };
  };

  const { labels, userTags, captions } = useMemo(
    () => getDataStrings(singleData, userTagsData),
    //eslint-disable-next-line
    [singleData, hoveredAssetData, userTagsData],
  );
  const mdType = useMemo(
    () =>
      getInitialMDType(
        globalMDType,
        userTags,
        labels,
        captions,
        entityParams,
        hideMetadataOptions,
      ),
    //eslint-disable-next-line
    [globalMDType],
  );
  const [cardMDType, setCardMDType] = useState<MetadataType>(mdType);

  //using a specific index for cart selections
  const specificIndex = `id-${
    singleData.cluster_id ? singleData.cluster_id : singleData.media_id
  }-index-${index}`;

  const isSingleObject = isDataPage
    ? false
    : singleData.type === "OBJECT" ||
      globalMDType === MetadataType.OBJECT_LABELS;

  const boundingBox = isDataCreationPage
    ? getCreationBoundingBoxes()
    : isSingleObject
      ? [singleData.bounding_box]
      : null;

  const videoURIForCluster = isClusterPage && singleData.video_uri;
  const videoURIsForData =
    isDataPage &&
    singleData.previews.map((preview: any) => preview.video_uri || "");

  const entityParam = searchParams.has(ENTITY_TYPE_PARAMETER)
    ? `?${ENTITY_TYPE_PARAMETER}=${searchParams.get(ENTITY_TYPE_PARAMETER)}`
    : "";

  const dynamicAssetPreviewEnabled =
    (indexOfPointerOut === -1 || indexOfPointerOut === index) &&
    !disableInteractivity;

  const hoverDelay = dynamicAssetPreviewEnabled ? 700 : 0;

  const endContentLoading = () => {
    if (index === 0 && isVertexCard) {
      return;
    }
    if (isNavigationClusterLoading || isCardslistLoading) {
      dispatch(setNavigationClusterLoader(false));
      dispatch(setCardslistLoading(false));
    }
  };

  const isSelected = useMemo(() => {
    if (singleData.flat_response) {
      return exportData.some(
        (data: any) => data.media_id === singleData.previews[0].media_id,
      );
    } else if (isDataPage) {
      return !!exportData.find(
        (singleExp: any) => singleExp.cluster_id === singleData.cluster_id,
      );
    } else if (isClusterPage) {
      const selectedParentCluster = exportData.find(
        (data: any) =>
          data.cluster_id === clusterID && data.exportType === "cluster",
      );
      if (selectedParentCluster) {
        //check if the media is selected to be removed from cluster selection
        const selectedForRemoval = selectedParentCluster.excludeMediaIDs
          ? selectedParentCluster.excludeMediaIDs.includes(singleData.media_id)
          : false;
        if (selectedForRemoval) {
          return false;
        }
        return true;
      } else {
        return !!exportData.find(
          (data: any) => data.media_id === singleData.media_id,
        );
      }
    } else if (inReview && inReviewDetails) {
      return !!exportData.find(
        (data: any) => data.media_id === singleData.media_id,
      );
    }
    return false;
    //eslint-disable-next-line
  }, [exportData, isDataPage, isClusterPage]);

  const cardVariant: CardVariant = isVertex
    ? CardVariant.VERTEX
    : isSelected
      ? CardVariant.SELECTED
      : CardVariant.DEFAULT;

  let finalTitle: string | JSX.Element;
  let titleIcon: JSX.Element;

  if (cardTitle) {
    finalTitle = cardTitle;
    titleIcon = <ImageIcon />;
  } else {
    const titleData = getTitleData(
      singleData,
      clusterId,
      datasetId,
      formattedFilename,
      isSelected,
      isVertex,
    );
    finalTitle = titleData.title;
    titleIcon = titleData.titleIcon;
  }

  const cardClassNames = classNames(styles.dataCard, className, {
    [styles.dataCardWithOutline]: isHovering || isReviewActionOpen,
    [styles.dataCardForSimilarity]: isVertex,
    [styles.dataCardForExport]: showSelectOption && isSelected && !isVertex,
  });

  const floatingDetailsClassnames = classNames(styles.floatingDetails, {
    [styles.selectedFloatingDetails]:
      singleData.floatingDetails !== "For Review",
  });

  const getRelevanceType = useCallback(
    (relevance: number) => {
      let relevanceType: RelevanceType | null = null;

      if (!relevanceScores) {
        return null;
      }

      if (relevanceScores.length === 3) {
        const H = relevanceScores[0];
        const M = relevanceScores[1];
        const L = relevanceScores[2];

        if (relevance >= 0 && relevance <= H) {
          relevanceType = "High";
        } else if (relevance > H && relevance <= M) {
          relevanceType = "Medium";
        } else if (relevance > M && relevance <= L) {
          relevanceType = "Low";
        }
      }
      return relevanceType;
    },
    [relevanceScores],
  );

  const getDynamicImages = useCallback(
    (type: "plain" | "thumb", previews: any) => {
      previews = previews?.length ? previews : [];
      return previews.map((preview: any) => {
        const relevanceType = getRelevanceType(preview.relevance_score || -1);
        return {
          id: preview.media_id,
          relevanceType,
          timestamp: preview.frame_timestamp,
          src: type === "plain" ? preview.media_uri : preview.media_thumb_uri,
        };
      }) as CardBodyImage[];
    },
    [getRelevanceType],
  );

  const plainImages = useMemo(
    () => getDynamicImages("plain", singleData.previews),
    [getDynamicImages, singleData.previews],
  );

  const thumbImages = useMemo(
    () => getDynamicImages("thumb", singleData.previews),
    [getDynamicImages, singleData.previews],
  );

  const relevanceType = getRelevanceType(singleData?.relevance_score || -1);
  const plainImage: CardBodyImage = {
    id: singleData.media_id,
    src: singleData.media_uri,
    relevanceType,
    timestamp: singleData.frame_timestamp,
  };
  const thumbImage: CardBodyImage = {
    id: singleData.media_id,
    src: singleData.media_thumb_uri,
    relevanceType,
    timestamp: singleData.frame_timestamp,
  };

  function getAssetData() {
    let assetData;

    if (hoveredAssetData.id) {
      if (isDataPage) {
        assetData = singleData.previews.find(
          (preview: any) => preview.media_id === hoveredAssetData.id,
        );
      } else {
        assetData =
          singleData.media_id === hoveredAssetData.id ? singleData : undefined;
      }
    }

    return assetData ?? {};
  }

  const setAssetPreviewFlag = (value: boolean) => {
    assetPreviewFlagRef.current = value;
  };

  const clearAssetData = () => {
    setHoveredAssetData({
      id: "",
      dimensions: { naturalHeight: 0, naturalWidth: 0 },
      error: false,
    });
  };

  const updateAssetData = async (id: string, source: string) => {
    if (inReview) {
      return;
    }
    const dimensions = {
      naturalWidth: LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
      naturalHeight: LOADING_OR_ERROR_CARD_DIMENSION_NUMBER,
    };

    try {
      const res = await getMediaDimensions(source);
      const result = { id, dimensions: res, error: false };

      if (assetPreviewFlagRef.current) {
        setHoveredAssetData((prev) => {
          const oldHeight = prev.dimensions.naturalHeight;
          const oldWidth = prev.dimensions.naturalWidth;

          const newHeight = res.naturalHeight;
          const newWidth = res.naturalWidth;

          // Normalize dimensions by the larger dimension to ensure consistent scale
          const normalizedOldHeight = oldHeight / Math.max(oldHeight, oldWidth);
          const normalizedOldWidth = oldWidth / Math.max(oldHeight, oldWidth);
          const normalizedNewHeight = newHeight / Math.max(newHeight, newWidth);
          const normalizedNewWidth = newWidth / Math.max(newHeight, newWidth);

          // Compare normalized dimensions to determine if new size is smaller
          if (
            normalizedNewHeight < normalizedOldHeight ||
            normalizedNewWidth < normalizedOldWidth
          ) {
            onIndexOfPointerOut(index);
          } else if (indexOfPointerOut > -1) {
            onIndexOfPointerOut(-1);
          }

          return result;
        });
      } else {
        clearAssetData();
      }
    } catch (error) {
      const result = { id, dimensions, error: true };
      if (assetPreviewFlagRef.current) {
        setHoveredAssetData(result);
      } else {
        clearAssetData();
      }
    }
  };

  const selectedLabels = useMemo(() => {
    if (cardMDType === MetadataType.USER_TAGS) {
      return datasetUserTags
        .filter((option) => option.selected)
        .map((option) => option.text);
    } else {
      return datasetLabels
        .filter((option) => option.selected)
        .map((option) => option.text);
    }
  }, [cardMDType, datasetUserTags, datasetLabels]);

  const showExportButton =
    !!(isSelected || isHovering) &&
    showSelectOption &&
    !isVertex &&
    !hoveredAssetData.id;

  const headerCardType: CardTypes = isObjectModel
    ? "OBJECT"
    : enrichDatasetId && isSingleEntityOnDataPage
      ? singleData?.type.slice(0, singleData?.type?.length - 1)
      : singleData?.type;

  const isObject = [CardTypes.OBJECT, CardTypes.OBJECTS].includes(
    headerCardType,
  ); //different from isObjectCard - to be replaced in further refactors
  const selectedMdIcon = getMDIcon(cardMDType);
  const mdOptions = getMdOptions(isObject, isCaptionsVisible);

  const debugInfo = useMemo(() => {
    return getDebugInfoText(
      clusterID,
      clusters,
      showDebugInfo,
      singleData,
      isClusterPage,
    );
  }, [clusterID, clusters, showDebugInfo, singleData, isClusterPage]);

  const handleViewClick = (type: MetadataType) => {
    setCardMDType(type);
    //eslint-disable-next-line
  };
  const handleViewAllClick = (type: MetadataType) => {
    onGlobalMDTypeChange(type);
    setCardMDType(type);
    //eslint-disable-next-line
  };

  const handleLinkClick = () => {
    if (linkClicked) return;
    else setLinkClicked(true);

    let newPath = location.pathname;
    if (isDataPage) {
      const imageCount = singleData?.previews?.length;
      if (imageCount === 1) {
        const firstPreview = singleData.previews[0];
        const isTypeImage = firstPreview.type === "IMAGE";
        const dynamicID = isTypeImage
          ? firstPreview.media_id
          : firstPreview.image_id;

        newPath += `/image/${dynamicID}${entityParam}`;

        if (!isTypeImage) {
          newPath = addQueryParameter(
            newPath,
            OBJECT_ID_QUERY_PARAMETER,
            firstPreview.media_id,
          );
        }
      } else {
        const tempParams = new URLSearchParams(searchParams.toString());
        tempParams.set(DATA_PAGE_PARAMETER, tempParams.get(PAGE_PARAMETER)!);
        tempParams.delete(PAGE_PARAMETER);
        const params = `?${tempParams.toString()}`;

        newPath += `/cluster/${clusterID}${params}`;
      }
    } else if (isClusterPage) {
      const dynamicID =
        singleData.type === "IMAGE" ? singleData.media_id : singleData.image_id;

      const queryParams = searchParams.toString() ? `?${searchParams}` : ``;

      let imagePath = `${location.pathname}/image/${dynamicID}${queryParams}`;

      if (isSingleObject) {
        imagePath = addQueryParameter(
          imagePath,
          OBJECT_ID_QUERY_PARAMETER,
          singleData.media_id,
        );
      }

      newPath = imagePath;
    }

    const url = window.location.host + newPath;
    copyToClipboard(url.toString());
  };

  const handleDetailsButtonClick = (e: any, data: Cluster) => {
    if (inReview && !!data.previews) {
      dispatch(setNavigationCluster(data));
    }
    if (data.onDetailsClick) {
      data.onDetailsClick(data);
      return;
    }

    if (e.target?.classList?.toString().includes("noDetailsClick")) {
      return;
    }

    if (isVertex) {
      return;
    }

    e.stopPropagation();
    clearAssetData();
    dispatch(clearImagePageParamsContext());

    const params = new URLSearchParams(location.search);

    if (isDataPage) {
      dispatch(setSimilarityData(null));
      dispatch(setNavigationCluster(null));
      dispatch(setDataPageScrollPosition(data.__id!));

      const imageCount = data?.previews?.length;
      if (imageCount === 1) {
        const firstPreview = data.previews[0];
        const isTypeImage = firstPreview.type === "IMAGE";
        const dynamicID = isTypeImage
          ? firstPreview.media_id
          : firstPreview.image_id;

        if (!data.flat_response) {
          dispatch(setNavigationCluster(data));
        }

        let basePath = `${location.pathname}${data.flat_response ? `/cluster/${data.cluster_id}` : ""}/image/${dynamicID}?${params.toString()}`;
        if (isTypeImage) {
          navigate(basePath);
        } else {
          const newPath = addQueryParameter(
            basePath,
            OBJECT_ID_QUERY_PARAMETER,
            firstPreview.media_id,
          );
          navigate(newPath);
        }
      } else {
        searchParams.set(
          DATA_PAGE_PARAMETER,
          searchParams.get(PAGE_PARAMETER) || "",
        );

        dispatch(
          fetchClusterPageIssueTypes(
            datasetId,
            clusterID,
            queryParamsFilters,
          ) as any,
        );

        searchParams.delete(PAGE_PARAMETER);
        const newPath = `${location.pathname}/cluster/${clusterID}`;
        const params = `?${searchParams.toString()}`;
        navigate(newPath.concat(params));
      }
    }

    if (isClusterPage) {
      dispatch(setClusterPageScrollPosition(singleData.__id));

      const dynamicID =
        singleData.type === "IMAGE" ? singleData.media_id : singleData.image_id;

      let imagePath = `${location.pathname}/image/${dynamicID}?${params.toString()}`;

      if (isSingleObject) {
        imagePath = addQueryParameter(
          imagePath,
          OBJECT_ID_QUERY_PARAMETER,
          singleData.media_id,
        );
      }

      dispatch(setSelectedCarouselIndex(index));
      //  TODO: fix
      navigate(imagePath);
    }
  };

  const handleSimilarButtonClick = () => {
    clearAssetData();
    if (onSetVertex) {
      if (isDataPage) {
        if (queryParamsFilters[FLAT_RESPONSE_PARAMETER]) {
          onSetVertex(singleData.previews[0].media_id, "MEDIA");
        } else {
          onSetVertex(clusterID, "CLUSTER");
        }
      } else {
        onSetVertex(singleData.media_id, "MEDIA");
      }
    }
    dispatch(clearImagePageParamsContext());
  };

  const handleVisualSimilarityApply = (threshold: string) => {
    if (onSetVertexSimilarity) {
      onSetVertexSimilarity(threshold);
    }
  };

  const handleAssetPreviewSetVertex = (negation?: boolean) => {
    clearAssetData();
    if (onSetVertex) {
      if (isSingleEntityOnDataPage) {
        onSetVertex(clusterID, "CLUSTER", negation);
      } else {
        onSetVertex(hoveredAssetData.id, "MEDIA", negation);
      }
    }
    dispatch(clearImagePageParamsContext());
  };

  const handleMouseEnter = (e: any) => {
    e.stopPropagation();
    setIsHovering(true);
    setAssetPreviewFlag(true);
    if (indexOfPointerOut === index) {
      onIndexOfPointerOut(-1);
    }
  };
  const handleMouseLeave = (e: any) => {
    e.stopPropagation();
    setIsHovering(false);
    setLinkClicked(false);
    setAssetPreviewFlag(false);
  };

  const handleMDLabelSelection = useCallback(
    (value: string) => {
      const updateLabels = (
        labels: FilterOption[],
        setLabelsAction: any,
        param: string,
        field: keyof FilterOption = "text",
      ) => {
        const selectedLabel = labels.find((option) => option.text === value);

        if (selectedLabel) {
          const newLabels = [
            ...labels.filter((option) => option.text !== value),
            { ...selectedLabel, selected: !selectedLabel.selected },
          ];
          if (param === LABELS_PARAMETER) {
            if (selectedLabel.selected) {
              const labelQuery = vqlQueries.find(
                (query): query is VqlQueryBase & LabelsFilter =>
                  isVqlQuery(query, "labels") &&
                  query.labels.op === "is" &&
                  query.labels.value.includes(value),
              );
              if (labelQuery) {
                // Remove the value from the query
                const updatedValues = labelQuery.labels.value.filter(
                  (label: string) => label !== value,
                );

                if (updatedValues.length > 0) {
                  // Update the query with the remaining values
                  updateVqlQuery({
                    ...labelQuery,
                    labels: {
                      ...labelQuery.labels,
                      value: updatedValues,
                    },
                  });
                } else {
                  // If no values left, remove the query entirely
                  removeVqlQuery(labelQuery.id);
                }
              }
            } else {
              addVqlQuery(
                createAndUpdateLabelsFilter([value], QueryOperator.IS),
              );
            }
          } else if (param === USER_TAGS_PARAMETER) {
            if (selectedLabel.selected) {
              const tagsQuery = vqlQueries.find(
                (query): query is VqlQueryBase & TagsFilter =>
                  isVqlQuery(query, "tags") &&
                  query.tags.op === "is" &&
                  query.tags.value.includes(selectedLabel.id.toString()),
              );
              if (tagsQuery) {
                // Remove the value from the query
                const updatedValues = tagsQuery.tags.value.filter(
                  (tag: string) => tag !== selectedLabel.id.toString(),
                );

                if (updatedValues.length > 0) {
                  // Update the query with the remaining values
                  updateVqlQuery({
                    ...tagsQuery,
                    tags: {
                      ...tagsQuery.tags,
                      value: updatedValues,
                    },
                  });
                } else {
                  // If no values left, remove the query entirely
                  removeVqlQuery(tagsQuery.id);
                }
              }
            } else {
              addVqlQuery(
                createAndUpdateUserTagsFilter(
                  [selectedLabel.id.toString()],
                  QueryOperator.IS,
                ),
              );
            }
          }

          if (setLabelsAction) {
            dispatch(setLabelsAction(newLabels) as any);
          }
        }
      };

      switch (cardMDType) {
        case MetadataType.OBJECT_CAPTIONS:
        case MetadataType.IMAGE_CAPTIONS:
          return;
        case MetadataType.USER_TAGS:
          updateLabels(datasetUserTags, setUserTags, USER_TAGS_PARAMETER, "id");
          break;
        case MetadataType.IMAGE_LABELS:
        case MetadataType.OBJECT_LABELS:
          updateLabels(datasetLabels, undefined, LABELS_PARAMETER);
          break;
      }

      onGlobalMDTypeChange(cardMDType);
    },
    //eslint-disable-next-line
    [cardMDType, searchParams, datasetLabels, datasetUserTags],
  );

  const renderActionButtons = () => {
    return (
      <div className={styles.actionButtons}>
        <Tooltip title={linkClicked ? "Copied!" : "Copy URL"}>
          <div
            onClick={handleLinkClick}
            className={classNames(styles.actionButton, styles.noDetailsClick)}
          >
            <LinkIcon clicked={linkClicked} />
          </div>
        </Tooltip>
        {isVertex ? (
          isVisualSimilarityEnabled &&
          isSearchByRelevanceEnabled && (
            <VisualSimilarityPopover onApply={handleVisualSimilarityApply} />
          )
        ) : (
          <div className={styles.actionsWrapper}>
            <Tooltip title="Find Similar">
              <div
                onClick={handleSimilarButtonClick}
                className={classNames(
                  styles.actionButton,
                  styles.noDetailsClick,
                )}
              >
                <FindSimilarIcon />
              </div>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const renderCardSelectionButtons = () => {
    if (
      disableInteractivity ||
      (searchParams.has(REVIEW_PARAMETER) &&
        !searchParams.has(LABEL_CLASS_PARAMETER))
    )
      return <noscript />;

    const wrapperClassNames = classNames(
      styles.noDetailsClick,
      styles.removeSimilarityBtn,
      styles.selectionButtonWrapper,
      {
        [styles.selectedWrapper]: isSelected || isVertex,
      },
    );

    if (isVertex) {
      return (
        <div onClick={onRemoveVertex} className={wrapperClassNames}>
          <div
            data-testid="remove-selection-button"
            className={styles.selectionButton}
          >
            <XIcon variant="new" />
          </div>
        </div>
      );
    } else if (showExportButton) {
      return (
        <div
          className={wrapperClassNames}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(e, isSelected, specificIndex, clusterID, singleData);
          }}
        >
          <div
            className={styles.selectionButton}
            data-testid="export-selection-button"
          >
            {isSelected && (
              <div
                data-testid="export-selected-button"
                className={styles.selection}
              >
                <CheckIcon />
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return <noscript />;
    }
  };

  const renderAssetPreview = () => {
    const assetData = getAssetData();
    const showCarousel =
      singleData.previews &&
      singleData.previews.length > 1 &&
      hoveredAssetData.id &&
      !hoveredAssetData.error;

    const enrichmentBoundingBoxes = assetData.bounding_box
      ? [assetData.bounding_box]
      : undefined;

    return (
      <AssetPreviewCard
        uniqueClusterWrapperId={uniqueClusterWrapperId}
        indexOfPointerOut={indexOfPointerOut}
        hoveredAssetData={hoveredAssetData}
        selectedLabels={selectedLabels}
        selectedMdIcon={selectedMdIcon}
        showCarousel={showCarousel}
        containerRef={containerRef}
        isSelected={isSelected}
        singleData={singleData}
        assetData={assetData}
        debugInfo={debugInfo}
        titleIcon={titleIcon}
        mdOptions={mdOptions}
        isVertex={isVertex}
        title={finalTitle}
        index={index}
        key={index}
        clearAssetData={clearAssetData}
        onUpdateAssetData={updateAssetData}
        onSetAssetPreviewFlag={setAssetPreviewFlag}
        onSetAssetPreviewVertex={handleAssetPreviewSetVertex}
        //enrichment data
        mdLabels={mdLabels}
        cardMDType={cardMDType}
        enrichmentBoundingBoxes={enrichmentBoundingBoxes}
        onViewClick={handleViewClick}
        onViewAllClick={handleViewAllClick}
        onMDLabelSelection={handleMDLabelSelection}
        columnsCount={columnsCount}
      />
    );
  };

  useEffect(() => {
    const type = getInitialMDType(
      globalMDType,
      userTags,
      labels,
      captions,
      entityParams,
      hideMetadataOptions,
    );
    if (type !== cardMDType) {
      setCardMDType(type);
    }
    //eslint-disable-next-line
  }, [globalMDType]);
  const labelFromQueryParam = useMemo(
    () => JSON.parse(queryParamsFilters[LABELS_PARAMETER] || "[]"),
    [queryParamsFilters],
  );

  const getContextualLabels = useCallback(
    (state: unknown) => getDatasetContextualLabels(state, labelFromQueryParam),
    [labelFromQueryParam],
  );

  const contextualLabels = useSelector(getContextualLabels);
  const metadataSourceValue = queryParamsFilters[METADATA_SOURCES_PARAMETER];

  const getMdLabelsByType = (type: MetadataType) => {
    switch (type) {
      case MetadataType.IMAGE_LABELS:
      case MetadataType.OBJECT_LABELS:
        if (metadataSourceValue) {
          const metadataSources = JSON.parse(metadataSourceValue || "[]");
          if (metadataSources.length) {
            const relevantLabels = contextualLabels
              .filter(({ source }) =>
                metadataSources.some((s: string) =>
                  s.toLowerCase().startsWith(String(source).toLowerCase()),
                ),
              )
              .map(({ text }) => text);
            return intersection(labels, relevantLabels);
          }
        } else {
          return labels;
        }
        break;
      case MetadataType.OBJECT_CAPTIONS:
      case MetadataType.IMAGE_CAPTIONS:
        return captions;
      case MetadataType.USER_TAGS:
        return userTags;
    }
    return [];
  };

  useEffect(() => {
    let result: string[] = [];

    // Set labels based on page type
    if (isDataCreationPage) {
      result = getCreationPageLabels();
    } else {
      result = getMdLabelsByType(cardMDType);
    }
    setMdLabels(result);
    //eslint-disable-next-line
  }, [
    metadataSourceValue,
    contextualLabels,
    width,
    showMetadataSummary,
    isDataCreationPage,
    globalMDType,
    cardMDType,
    userTagsData,
    hoveredAssetData,
    getMdLabelsByType,
  ]);

  const displaySuffix = useSelector(getIsAddMediaEnabled);

  const cardHeaderMap: Record<"default" | "review", JSX.Element> = {
    default: (
      <CardHeader
        index={index}
        suffix={
          (singleData.is_pending ||
            (isClusterPage && navigationCluster?.is_pending)) &&
          displaySuffix
            ? {
                icon: <ReloadIcon />,
                tooltipText: "New media – full indexing required",
              }
            : undefined
        }
        title={finalTitle}
        mdLabels={mdLabels}
        cardVariant={cardVariant}
        prefix={titleIcon}
        debugInfo={debugInfo}
        mdOptions={mdOptions}
        cardMdType={cardMDType}
        selectedLabels={selectedLabels}
        selectedMdIcon={selectedMdIcon}
        onViewClick={handleViewClick}
        onViewAllClick={handleViewAllClick}
        onMDLabelSelection={
          isBodyUnclickable ? undefined : handleMDLabelSelection
        }
      />
    ),
    review: (
      <ReviewCardHeader
        title={finalTitle as string}
        titleIcon={titleIcon}
        cardVariant={cardVariant}
        reviewClassLabel={singleData.label}
        floatingDetails={singleData.floatingDetails}
        isPreview={isPreview}
      />
    ),
  };

  const isTall = useMemo(() => {
    return false;
  }, []);

  return (
    <EnrichmentCardProvider
      getMdLabelsByType={getMdLabelsByType}
      isSingleItem={plainImages.length <= 1}
    >
      <div
        ref={setNodeRef}
        id={uniqueClusterWrapperId}
        key={uniqueClusterWrapperId}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          border: singleData.borderColor
            ? `${getVLColorByActionColor[singleData.borderColor as PreviewBorderColor]} 3px solid`
            : undefined,
        }}
        className={classNames(styles.singleClusterWrapper, {
          [styles.selected]: isSelected,
          [styles.tall]: isTall,
          [styles.reviewClusters]: inReview && !inReviewDetails,
        })}
        onClick={(e: any) => {
          if (!hideMetadataOptions) {
            handleDetailsButtonClick(e, singleData);
          }
        }}
        {...listeners}
        {...attributes}
      >
        <div
          className={classNames(styles.extendedArea, styles.noDetailsClick)}
          onMouseEnter={() => setIsHovering(true)}
        ></div>
        <div className={cardClassNames}>
          {cardHeaderMap[inReview ? "review" : "default"]}
          {singleData.actionList && showFeedbackInCard && (
            <div
              className={classNames(styles.reviewActionWrapper, {
                [styles.reviewActionWrapperOpen]: isReviewActionOpen,
              })}
            >
              <div className={styles.reviewActionListInner}>
                <VLFloatGroup
                  actionList={singleData.actionList}
                  placement="bottomRight"
                  tooltipPlacement="bottom"
                  onOpenChange={setIsReviewActionOpen}
                />
              </div>
            </div>
          )}

          {showFloatingDetails && (
            <div className={floatingDetailsClassnames}>
              <ReviewFloatingDetails
                floatingDetails={singleData.floatingDetails}
                className={classNames(styles.roundedFloatingDetails)}
              />
            </div>
          )}
          <CardBody
            opacity={singleData.opacity}
            isHovering={isHovering}
            plainImage={plainImage}
            thumbImage={thumbImage}
            hoverDelay={hoverDelay}
            mdLabels={mdLabels || []}
            plainImages={plainImages}
            thumbImages={thumbImages}
            boundingBox={boundingBox}
            videoURIs={videoURIsForData}
            bodyEntityType={bodyEntityType}
            unClickable={isBodyUnclickable}
            isSingleObject={isSingleObject}
            videoURIForCluster={videoURIForCluster}
            isAssetPreviewEnabled={dynamicAssetPreviewEnabled && !inReview}
            onUpdateAssetData={updateAssetData}
            onEndContentLoading={endContentLoading}
            inReview={inReview}
          />

          {renderCardSelectionButtons()}
          {showActionButtons && !inReview && renderActionButtons()}
        </div>
        {dynamicAssetPreviewEnabled && renderAssetPreview()}
      </div>
    </EnrichmentCardProvider>
  );
};

export default EnrichmentCard;
